// @ts-strict-ignore
import * as React from 'react';
import { RootState } from '../reducers/reducers';
import { useDispatch, useSelector } from 'react-redux';
import {
    automationIsActive,
    getAutomationIdByType,
    getReleaseStepFromState,
    googleAppIsReleased,
    isExternalBuildBetaTesting,
    isFirebaseBetaIntegration,
    isGoogleBetaIntegration,
    isInternalBuildBetaTesting,
    isRunwayBuildDistroBetaIntegration,
    isTestFlightBetaIntegration,
} from '../helpers';
import styles from './styles/stepStyles';
import { AppSettingsUpdatable, ReleaseUpdatable } from '../actions/updatable';
import { ReleaseStepEndBetaSoakRequest, tryUpdateRelease } from '../actions/release';
import {
    AutomationType,
    BetaBuild,
    BetaSoakStatus,
    BetaTestingStyle,
    BuildInfo,
    GoogleBetaBuildDetail,
    ReleaseStepBetaTesting,
    ReleaseStepBetaTestingData,
    RunwayResponseType,
    TestFlightBetaBuildDetail,
} from '../types';
import { tryUpdateAppSettings, UpdateBetaStyleRequest } from '../actions/appSettings';
import { FeatureSection } from '../typescriptHelpers';
import { ReleaseStepInactiveContext } from '../contexts';
import Layout, { Content, Footer } from 'antd/lib/layout/layout';
import BetaLatestBuild from './BetaLatestBuild';
import BetaTestingActiveBuild from './betaTesting/BetaTestingActiveBuild';
import BetaTestingHistory from './BetaTestingHistory';
import {
    colorForBetaBuildStatus,
    displayColorForBetaSoakStatus,
    displayDescriptionForAppleExternalBuildState,
    displayDescriptionForAppleInternalBuildState,
    displayDescriptionForBetaSoakStatus,
    displayDescriptionForBetaSoakStatusMultipleBuilds,
    displayDescriptionForGoogleBetaBuildState,
    displayTextForAppleExternalBetaBuildState,
    displayTextForAppleInternalBetaBuildState,
    displayTextForBetaSoakStatus,
    displayTitleForGoogleBetaBuildState,
} from '../helpers/displayHelpers';
import { RunwayUserAction } from '../helpers/aclHelpers';
import {
    selectAppSettingAutomations,
    selectAppSettings,
    selectIsAutomationEnabled,
    selectStepRun,
} from '../selectors/selectors';
import { compact, reduce } from 'lodash';
import { antGrey, runwayColors } from './styles/globalStyles';
import { globalMessage } from '../utils/globalMessage';
import { EmptyStateWrapper } from './releaseEmptyState/EmptyStateWrapper';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { BuildsList } from './buildDistribution/CommonComponents';
import { BuildDetailsDrawer } from './buildDistribution/BuildDetailsDrawer';
import Text from 'antd/lib/typography/Text';
import {
    useCallUpdatable,
    useError,
    useIsLoading,
    useTestingTrackFromGoogleBetaIntegration,
    useUpdated,
} from '@/utils/hooks/helpers';
import { StepConfigUpdatable } from '@/actions/stepConfig';
import { StepRunUpdatable } from '@/actions/stepRun';
import { BetaTestingStepSettings } from '@/types/stepConfig';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import { useLocation } from 'react-router-dom';

export const StepContentBetaTesting: React.FC<ReleaseStepOrStepRunComponentProps> = (props) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const isStepInactive = React.useContext(ReleaseStepInactiveContext);
    const focusedFeedbackId = new URLSearchParams(location.search).get('betaFeedbackId');

    const [selectedBuildId, setSelectedBuildId] = React.useState<string>(null);
    const [selectedBucketId, setSelectedBucketId] = React.useState<string>(null);

    const releaseStep = useSelector(
        (state: RootState) =>
            getReleaseStepFromState(props.releaseStepType, props.releaseId, state) as ReleaseStepBetaTesting,
    );

    const automations = useSelector((state: RootState) => selectAppSettingAutomations(state, props.appId));

    // TODO: #WORKFLOWS# this is a temporary fix, what is the best way to get automation ids that apply to multiple steps?
    const autoMergeRunwayPRsAutomationId = automations.find(
        (automation) => automation.type === AutomationType.AutoMergeRunwayPRs,
    )?.id;

    const isAutoMergeRunwayPRsEnabled = useSelector((state: RootState) =>
        selectIsAutomationEnabled(state, props.appId, autoMergeRunwayPRsAutomationId),
    );

    const stepRunId = props.stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const stepData: ReleaseStepBetaTestingData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;

    const activeCIBuild = stepData.activeCIBuild;
    const betaIntegrationType = stepData.betaIntegrationType;
    const buildSystemIntegrationType = stepData.buildSystemIntegrationType;
    const betaTestingActiveBuildIdentifier = stepData.betaTestingActiveBuildIdentifier;
    const consoleUrl = stepData.consoleUrl;

    const appSettings = useSelector((state: RootState) => selectAppSettings(state, props.appId));

    const allIntegrationInstallationIds = appSettings.integrations.map((integration) => integration.installationId);

    const stepConfigSettings = stepRun?.stepConfigSnapshot?.settings as BetaTestingStepSettings;

    const defaultTestTrack = stepConfigSettings?.googleTestingTrackName;

    const stepConfigIntegrationInstallationIds = stepRun?.stepConfigSnapshot?.integrationInstallationIds;

    const automationRuns = stepRun?.automationRuns ?? releaseStep?.automationRuns;

    const autoAssignBetaGroupsEnabled = useSelector((state: RootState) =>
        selectIsAutomationEnabled(
            state,
            props.appId,
            getAutomationIdByType(automationRuns, AutomationType.AutoAssignBetaBuilds),
        ),
    );

    const autoApplyBetaBuildNotesAutomation = automationRuns?.find(
        (automationRun) => automationRun.type === AutomationType.AutoApplyBetaBuildNotes,
    );

    const autoApplyBetaBuildNotesAutomationIsActive =
        autoApplyBetaBuildNotesAutomation ? automationIsActive(autoApplyBetaBuildNotesAutomation) : false;

    const fallbackDefaultTestTrack = useTestingTrackFromGoogleBetaIntegration(props.appId);

    const settings: BetaTestingStepSettings =
        stepConfigSettings ? stepConfigSettings : (
            {
                betaTestingStyle: appSettings.betaTestingStyle,
                prescribedBetaSoakDurationSeconds: appSettings.prescribedBetaSoakDurationSeconds,
                googleTestingTrackName: fallbackDefaultTestTrack,
                upstreamCIStepConfigId: null,
                upstreamCIStepConfigOptions: {},
            }
        );

    const [
        updateBetaTestingStyle,
        updateBetaTestingStyleIsLoading,
        updateBetaTestingStyleResponseType,
        updateBetaTestingStyleError,
    ] = useCallUpdatable(StepConfigUpdatable.UpdateBetaTestingStyle, props.stepRunComponentProps);

    const releaseStepSoakPeriodEndIsUpdating = useIsLoading(ReleaseUpdatable.EndBetaSoak, props.releaseId);
    const releaseStepSoakPeriodEndError = useError(ReleaseUpdatable.EndBetaSoak, props.releaseId);

    const [endBetaSoak, endBetaSoakIsLoading, , endBetaSoakError] = useCallUpdatable(
        StepRunUpdatable.EndBetaSoakPeriod,
        props.stepRunComponentProps,
    );

    const soakPeriodEndIsUpdating =
        props.stepRunComponentProps ? endBetaSoakIsLoading : releaseStepSoakPeriodEndIsUpdating;
    const soakPeriodEndError = props.stepRunComponentProps ? endBetaSoakError : releaseStepSoakPeriodEndError;

    // TODO: #WORKFLOWS# Remove release step actions
    const releaseStepBetaStyleIsUpdating = useIsLoading(AppSettingsUpdatable.BetaStyle);
    const releaseStepBetaStyleUpdated = useUpdated(AppSettingsUpdatable.BetaStyle);
    const releaseStepBetaStyleUpdateError = useError(AppSettingsUpdatable.BetaStyle);

    const betaStyleIsUpdating =
        props.stepRunComponentProps ? updateBetaTestingStyleIsLoading : releaseStepBetaStyleIsUpdating;
    const betaStyleUpdated =
        props.stepRunComponentProps ?
            updateBetaTestingStyleResponseType === RunwayResponseType.Success
        :   releaseStepBetaStyleUpdated;
    const betaStyleUpdateError =
        props.stepRunComponentProps ? updateBetaTestingStyleError : releaseStepBetaStyleUpdateError;

    const saveBetaStyle = (input: UpdateBetaStyleRequest) => {
        if (props.stepRunComponentProps) {
            updateBetaTestingStyle(input);
        } else {
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.BetaStyle, input));
        }
    };

    const endBetaTestingSoak = (request: ReleaseStepEndBetaSoakRequest) => {
        if (props.stepRunComponentProps) {
            endBetaSoak(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.EndBetaSoak, request, request.releaseId));
        }
    };

    React.useEffect(() => {
        if (soakPeriodEndError) {
            globalMessage.error(soakPeriodEndError);
        }
    }, [soakPeriodEndError]);

    React.useEffect(() => {
        if (betaStyleUpdated) {
            globalMessage.success('Beta style updated!');
        }
    }, [betaStyleUpdated]);

    React.useEffect(() => {
        if (betaStyleUpdateError) {
            globalMessage.error(betaStyleUpdateError);
        }
    }, [betaStyleUpdateError]);

    const betaStyle = settings.betaTestingStyle;
    const soakDurationInSeconds = settings.prescribedBetaSoakDurationSeconds;
    const soakStartedAt = stepData.soakStartedAt;
    const soakFinishedAt = stepData.soakFinishedAt;

    const soakStarted = stepData.soakStartedAt != null;
    const soakFinished = stepData.soakFinishedAt != null;
    const soakDidFinishEarly = stepData.soakDidFinishEarly;

    const showElapsedTime =
        (betaStyle == BetaTestingStyle.OpenEndedSoak || betaStyle == BetaTestingStyle.NonBlocking) &&
        soakFinished == false &&
        soakStarted == true;
    const showSoakTimeRemaining =
        betaStyle == BetaTestingStyle.TimeLimitedSoak && soakFinished === false && soakStarted == true;

    const betaGroups = stepData.betaGroups ?? [];
    const betaTesters = stepData.testers ?? [];
    const betaBuilds = stepData.betaBuilds ?? [];
    const testTracks = stepData.testTracks ?? [];

    const activeBetaBuilds = betaBuilds.filter((betaBuild) => betaBuild.isSelectedBuild);

    let betaSoakStatus = BetaSoakStatus.NotTesting;

    if (soakDidFinishEarly || soakFinishedAt) {
        betaSoakStatus = BetaSoakStatus.TestingComplete;
    } else if (soakStartedAt) {
        betaSoakStatus = BetaTestingStyle.NonBlocking ? BetaSoakStatus.TestingNonBlocking : BetaSoakStatus.Testing;
    }

    const userId = useSelector((state: RootState) => state.user.id);

    const onEndBetaTestingSoak = () => {
        const endBetaSoakRequest: ReleaseStepEndBetaSoakRequest = {
            orgId: props.orgId,
            appId: props.appId,
            userId,
            releaseId: props.releaseId,
        };

        endBetaTestingSoak(endBetaSoakRequest);
    };

    const getGoogleBetaBuildTitleDescriptionColor = (
        activeBetaBuilds: BetaBuild[],
        betaSoakStatus: BetaSoakStatus,
        betaTrackName?: string,
    ) => {
        if (activeBetaBuilds.length === 1) {
            const activeBetaBuild = activeBetaBuilds[0];
            const appStoreBuild = activeBetaBuild.appStoreBuild.appStoreBuild;
            const betaDetail = activeBetaBuild.betaBuildDetails as GoogleBetaBuildDetail;

            const isActivelyBetaTesting = googleAppIsReleased(betaDetail.status);

            const buildStatusTitleText = displayTitleForGoogleBetaBuildState(betaDetail.status);
            const buildStatusDescriptionText = displayDescriptionForGoogleBetaBuildState(
                betaTrackName ?? 'testing',
                betaDetail.status,
                appStoreBuild.buildNumber,
                betaDetail.userFraction,
            );

            const title = isActivelyBetaTesting ? displayTextForBetaSoakStatus(betaSoakStatus) : buildStatusTitleText;

            const description =
                isActivelyBetaTesting ?
                    displayDescriptionForBetaSoakStatus(betaSoakStatus, appStoreBuild.buildNumber)
                :   buildStatusDescriptionText;

            const color =
                isActivelyBetaTesting ?
                    displayColorForBetaSoakStatus(betaSoakStatus)
                :   colorForBetaBuildStatus(activeBetaBuild.betaStatus);

            return {
                title,
                color,
                description,
                isExternalBuildStatus: null,
            };
        } else if (activeBetaBuilds.length > 1) {
            const allBuildsAreActivelyTesting = reduce(
                activeBetaBuilds,
                (allAreTesting, activeBetaBuild) => {
                    if (allAreTesting) {
                        const betaDetail = activeBetaBuild.betaBuildDetails as GoogleBetaBuildDetail;

                        return googleAppIsReleased(betaDetail.status);
                    } else {
                        return allAreTesting;
                    }
                },
                true,
            );

            if (allBuildsAreActivelyTesting) {
                const title = displayTextForBetaSoakStatus(betaSoakStatus);
                const buildNumbers = activeBetaBuilds.map(
                    (build) => `#${build.appStoreBuild.appStoreBuild.buildNumber}`,
                );
                const description = displayDescriptionForBetaSoakStatusMultipleBuilds(betaSoakStatus, buildNumbers);
                const color = displayColorForBetaSoakStatus(betaSoakStatus);

                return {
                    title,
                    color,
                    description,
                    isExternalBuildStatus: null,
                };
            } else {
                const nonTestingBuilds = activeBetaBuilds.filter((build) => {
                    const betaDetail = build.betaBuildDetails as GoogleBetaBuildDetail;

                    return !googleAppIsReleased(betaDetail.status);
                });

                // we should have at least one
                const nonTestingBuild = nonTestingBuilds[0];
                const appStoreBuild = nonTestingBuild.appStoreBuild;
                const betaDetail = nonTestingBuild.betaBuildDetails as GoogleBetaBuildDetail;

                const title = displayTitleForGoogleBetaBuildState(betaDetail.status);
                const description = displayDescriptionForGoogleBetaBuildState(
                    betaTrackName ?? 'testing',
                    betaDetail.status,
                    appStoreBuild.appStoreBuild.buildNumber,
                    betaDetail.userFraction,
                );

                const color = colorForBetaBuildStatus(nonTestingBuild.betaStatus);

                return {
                    title,
                    color,
                    description,
                    isExternalBuildStatus: null,
                };
            }
        }

        return null;
    };

    const getRunwayBuildDistroBetaBuildTitleDescriptionColor = (
        activeBetaBuilds: BetaBuild[],
        betaSoakStatus: BetaSoakStatus,
    ) => {
        // runway build distro only supports 1 active beta build

        const activeBetaBuild = activeBetaBuilds[0];
        const appStoreBuild = activeBetaBuild.appStoreBuild.appStoreBuild;

        // assume for now that all build distro builds are always in testing
        const title = displayTextForBetaSoakStatus(betaSoakStatus);
        const description = displayDescriptionForBetaSoakStatus(betaSoakStatus, appStoreBuild.buildNumber);
        const color = displayColorForBetaSoakStatus(betaSoakStatus);

        return {
            title,
            color,
            description,
            isExternalBuildStatus: null,
        };
    };

    const getFirebaseBetaBuildTitleDescriptionColor = (
        activeBetaBuilds: BetaBuild[],
        betaSoakStatus: BetaSoakStatus,
    ) => {
        if (activeBetaBuilds.length === 0) {
            return null;
        }

        // supporting a single beta build, to be improved
        const activeBetaBuild = activeBetaBuilds[0];
        const appStoreBuild = activeBetaBuild.appStoreBuild.appStoreBuild;

        // assume for now that all firebase app distribution builds are always in testing
        const title = displayTextForBetaSoakStatus(betaSoakStatus);
        const description = displayDescriptionForBetaSoakStatus(betaSoakStatus, appStoreBuild.buildNumber);
        const color = displayColorForBetaSoakStatus(betaSoakStatus);

        return {
            title,
            color,
            description,
            isExternalBuildStatus: null,
        };
    };

    const betaTitleColorDescriptionBuildStatus = (
        activeBetaBuilds: BetaBuild[],
        betaSoakStatus: BetaSoakStatus,
        activeCIBuild?: BuildInfo,
        betaTrackName?: string,
    ) => {
        if (activeBetaBuilds.length > 0) {
            const activeBuildsIntegrationType = activeBetaBuilds[0].appStoreBuild.appStoreBuild.integrationId;
            if (isTestFlightBetaIntegration(activeBuildsIntegrationType)) {
                const activeBetaBuild = activeBetaBuilds[0];
                const considerAsExternal = activeBetaBuild.isConsideredExternal;

                if (!activeBetaBuild.betaBuildDetails)
                    // no beta build details
                    return {
                        title: 'Beta Testing',
                        color: runwayColors.statusYellow,
                        description: '',
                        isExternalBuildStatus: null,
                    };

                const betaBuildDetails = activeBetaBuild.betaBuildDetails as TestFlightBetaBuildDetail;

                const betaBuildExternalBuildState = betaBuildDetails.externalBuildState;
                const betaBuildInternalBuildState = betaBuildDetails.internalBuildState;

                const isActivelyBetaTesting =
                    considerAsExternal ?
                        isExternalBuildBetaTesting(betaBuildExternalBuildState)
                    :   isInternalBuildBetaTesting(betaBuildInternalBuildState);
                const buildStatusTitleText =
                    considerAsExternal ?
                        displayTextForAppleExternalBetaBuildState(betaBuildExternalBuildState)
                    :   displayTextForAppleInternalBetaBuildState(betaBuildInternalBuildState);

                const buildStatusDescriptionText =
                    considerAsExternal ?
                        displayDescriptionForAppleExternalBuildState(
                            activeBetaBuild.appStoreBuild.appStoreBuild.buildNumber,
                            betaBuildExternalBuildState,
                        )
                    :   displayDescriptionForAppleInternalBuildState(
                            activeBetaBuild.appStoreBuild.appStoreBuild.buildNumber,
                            betaBuildInternalBuildState,
                        );

                const title =
                    isActivelyBetaTesting ? displayTextForBetaSoakStatus(betaSoakStatus) : buildStatusTitleText;

                const description =
                    isActivelyBetaTesting ?
                        displayDescriptionForBetaSoakStatus(
                            betaSoakStatus,
                            activeBetaBuild.appStoreBuild.appStoreBuild.buildNumber,
                        )
                    :   buildStatusDescriptionText;

                const color =
                    isActivelyBetaTesting ?
                        displayColorForBetaSoakStatus(betaSoakStatus)
                    :   colorForBetaBuildStatus(activeBetaBuild.betaStatus);

                return {
                    title,
                    color,
                    description,
                    isExternalBuildStatus: isActivelyBetaTesting ? null : considerAsExternal,
                };
            } else if (isGoogleBetaIntegration(activeBuildsIntegrationType)) {
                return getGoogleBetaBuildTitleDescriptionColor(activeBetaBuilds, betaSoakStatus, betaTrackName);
            } else if (isRunwayBuildDistroBetaIntegration(activeBuildsIntegrationType)) {
                return getRunwayBuildDistroBetaBuildTitleDescriptionColor(activeBetaBuilds, betaSoakStatus);
            } else if (isFirebaseBetaIntegration(activeBuildsIntegrationType)) {
                return getFirebaseBetaBuildTitleDescriptionColor(activeBetaBuilds, betaSoakStatus);
            } else {
                // unknown beta integration
                return {
                    title: 'Beta Testing',
                    color: runwayColors.statusYellow,
                    description: '',
                    isExternalBuildStatus: null,
                };
            }
        } else if (activeCIBuild) {
            return {
                title: displayTextForBetaSoakStatus(betaSoakStatus),
                color: displayColorForBetaSoakStatus(betaSoakStatus),
                description: displayDescriptionForBetaSoakStatus(betaSoakStatus, activeCIBuild.buildIdentifier),
                isExternalBuildStatus: null,
            };
        } else {
            return {
                title: displayTextForBetaSoakStatus(BetaSoakStatus.NotTesting),
                color: displayColorForBetaSoakStatus(BetaSoakStatus.NotTesting),
                description: displayDescriptionForBetaSoakStatus(BetaSoakStatus.NotTesting),
                isExternalBuildStatus: null,
            };
        }
    };

    const { title, color, description, isExternalBuildStatus } = betaTitleColorDescriptionBuildStatus(
        activeBetaBuilds,
        betaSoakStatus,
        activeCIBuild,
        betaTestingActiveBuildIdentifier,
    );
    const isNonBlockingSoak = betaStyle == BetaTestingStyle.NonBlocking;

    const buildDistroBuilds = compact(betaBuilds.map((betaBuild) => betaBuild.buildDistroBuild));

    return (
        <>
            <Layout>
                <Content
                    style={{
                        ...styles.innerContent,
                        ...styles.spaceBetween,
                        ...styles.contentWithFooterStyle,
                        paddingTop: 0,
                    }}
                >
                    <BetaLatestBuild
                        stepRunComponentProps={props.stepRunComponentProps}
                        activeBuildStatusString={title}
                        activeBuildStatusDescription={description}
                        isExternalBuildStatus={isExternalBuildStatus}
                        titleColor={color}
                        soakStartedAt={soakStartedAt}
                        soakFinishedAt={soakFinishedAt}
                        soakDidFinishEarly={soakDidFinishEarly}
                        appId={props.appId}
                        orgId={props.orgId}
                        releaseId={props.releaseId}
                        releaseStepType={props.releaseStepType}
                    />
                    <BetaTestingActiveBuild
                        autoAssignBetaGroupsEnabled={autoAssignBetaGroupsEnabled}
                        stepRunComponentProps={props.stepRunComponentProps}
                        integrationInstallationIds={
                            stepConfigIntegrationInstallationIds ?? allIntegrationInstallationIds
                        }
                        autoApplyBetaBuildNotesAutomationIsActive={autoApplyBetaBuildNotesAutomationIsActive}
                        releaseStepType={props.releaseStepType}
                        orgId={props.orgId}
                        appId={props.appId}
                        releaseId={props.releaseId}
                        betaIntegrationType={betaIntegrationType}
                        betaStyle={betaStyle}
                        soakDurationInSeconds={soakDurationInSeconds}
                        betaTestingActiveBuildIdentifier={betaTestingActiveBuildIdentifier}
                        consoleUrl={consoleUrl}
                        defaultTestTrack={defaultTestTrack}
                        soakFinished={soakFinished}
                        showSoakTimeRemaining={showSoakTimeRemaining}
                        soakStartedAt={soakStartedAt}
                        showElapsedTime={showElapsedTime}
                        betaStyleIsUpdating={betaStyleIsUpdating}
                        saveBetaStyle={saveBetaStyle}
                        betaStyleUpdated={betaStyleUpdated}
                        activeBetaBuilds={activeBetaBuilds}
                        betaFeedback={stepData.betaFeedback}
                        focusedFeedbackId={focusedFeedbackId}
                        testers={betaTesters}
                        testTracks={testTracks}
                        groups={betaGroups}
                        activeCIBuild={activeCIBuild}
                        onViewBuildDetails={(build) => {
                            setSelectedBuildId(build.id);
                            setSelectedBucketId(build.bucketId);
                        }}
                    />
                    <EmptyStateWrapper
                        releaseStepType={props.releaseStepType}
                        appId={props.appId}
                        releaseId={props.releaseId}
                        featureSections={[FeatureSection.BetaTestingBetaBuildHistory]}
                        shouldForceDisable={betaBuilds.length > 0}
                    >
                        <div style={{ width: '100%' }}>
                            {buildDistroBuilds?.length > 0 ?
                                <div
                                    style={{
                                        marginTop: 20,
                                        flexDirection: 'column',
                                        display: 'flex',
                                        width: '100%',
                                        gap: 12,
                                    }}
                                >
                                    <Text style={{ fontSize: 20, color: antGrey.grey7 }}>History</Text>
                                    <BuildsList
                                        appId={props.appId}
                                        orgId={props.orgId}
                                        builds={buildDistroBuilds}
                                        shouldHideBranch
                                        setSelectedBuildId={(buildId) => {
                                            const buildBucketId = buildDistroBuilds?.find(
                                                (build) => build.id === buildId,
                                            )?.bucketId;

                                            setSelectedBuildId(buildId);
                                            setSelectedBucketId(buildBucketId);
                                        }}
                                    />
                                </div>
                            :   <BetaTestingHistory
                                    stepRunComponentProps={props.stepRunComponentProps}
                                    autoApplyBetaBuildNotesAutomationIsActive={
                                        autoApplyBetaBuildNotesAutomationIsActive
                                    }
                                    autoAssignBetaGroupsEnabled={autoAssignBetaGroupsEnabled}
                                    orgId={props.orgId}
                                    appId={props.appId}
                                    releaseId={props.releaseId}
                                    betaBuilds={betaBuilds}
                                    betaGroups={betaGroups}
                                    betaTesters={betaTesters}
                                    testTracks={testTracks}
                                    betaIntegrationType={betaIntegrationType}
                                    defaultTestTrack={settings.googleTestingTrackName ?? 'testing'}
                                    betaFeedback={stepData.betaFeedback ?? []}
                                    focusedFeedbackId={focusedFeedbackId}
                                />
                            }
                        </div>
                    </EmptyStateWrapper>
                </Content>
                <Footer
                    style={{
                        ...styles.footerStyle,
                    }}
                >
                    <PermissionedActionButton
                        releaseId={props.releaseId}
                        actionType={RunwayUserAction.UAApproveBetaTesting}
                        type="primary"
                        disabled={soakFinished || isStepInactive || isNonBlockingSoak}
                        loading={soakPeriodEndIsUpdating}
                        onClick={onEndBetaTestingSoak}
                    >
                        End beta soak
                    </PermissionedActionButton>
                </Footer>
            </Layout>
            {selectedBuildId && (
                <BuildDetailsDrawer
                    buildId={selectedBuildId}
                    bucketId={selectedBucketId}
                    isAutoMergeRunwayPRsEnabled={isAutoMergeRunwayPRsEnabled}
                    buildSystemIntegrationType={buildSystemIntegrationType}
                    onCancel={() => {
                        setSelectedBuildId(null);
                        setSelectedBucketId(null);
                    }}
                    appId={props.appId}
                    orgId={props.orgId}
                />
            )}
        </>
    );
};
